/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Row } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { api, ApiError } from "core/api";
import { Button, Card, Form, Input, Switch } from "core/components";
import { navigateTo } from "core/navigation";
import { UnitModelFormData, UnitModelSchema } from "core/schema";
import { admin_unit_models_path, admin_unit_model_path } from "routes";

type UnitModelFormProps = {
  unit_model: UnitModelFormData;
};

const UnitModelForm = ({ unit_model }: UnitModelFormProps): JSX.Element => {
  const {
    control,
    register,
    handleSubmit,
    formState: { isSubmitting, errors }
  } = useForm<UnitModelFormData>({
    mode: "onChange",
    defaultValues: UnitModelSchema.cast(unit_model),
    resolver: yupResolver(UnitModelSchema)
  });
  const [formErrors, setFormErrors] = React.useState<Array<string>>([]);

  const save = async (data: UnitModelFormData): Promise<unknown> => {
    try {
      if (data.id) {
        await api.put(admin_unit_model_path(unit_model), data);
      } else {
        await api.post(admin_unit_models_path(unit_model), data);
      }

      navigateTo(admin_unit_models_path());
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    }
  };

  return (
    <React.StrictMode>
      <Form errors={formErrors}>
        <Card>
          <Card.Header title={unit_model.id ? "Edit Unit of Model" : "New Unit of Model"}>
            <Button
              icon="arrow-left"
              iconProps={{ className: "mr-3" }}
              className="button--default mr-3"
              disabled={isSubmitting}
              title="Back"
              onClick={() => navigateTo(admin_unit_models_path())}
            />
            <Button
              icon="save"
              iconProps={{ className: "mr-3" }}
              className="button--brand mr-3"
              disabled={isSubmitting}
              title="Save"
              onClick={handleSubmit(async (data) => save(data))}
            />
          </Card.Header>
          <Card.Body>
            <Container>
              <Row>
                <Col lg={3} />
                <Col>
                  <Controller
                    name="active"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        {...field}
                        label="Active"
                        info="Active or Inactive"
                        checked={field.value}
                        value={field.value?.toString()}
                        errorMessage={errors.active?.message}
                      />
                    )}
                  />
                  <Input
                    {...register("code")}
                    label="Code"
                    required
                    errorMessage={errors.code?.message}
                    className="field text-uppercase"
                    maxLength={10}
                  />
                  <Input {...register("name")} label="Name" required errorMessage={errors.name?.message} maxLength={100} />
                </Col>
                <Col lg={3} />
              </Row>
            </Container>
          </Card.Body>
        </Card>
      </Form>
    </React.StrictMode>
  );
};

export default UnitModelForm;
